import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
    useTheme,
} from '@material-ui/core';

import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import DownloadIcon from '@material-ui/icons/InsertDriveFile';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';

const useStyles1 = makeStyles((theme) => ({
    root: {
        flexShrink: 0,
        marginLeft: theme.spacing(2.5),
    },
}));

function TablePaginationActions(props) {
    const classes = useStyles1();
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <div className={classes.root}>
            <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page">
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page">
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </div>
    );
}

const DefaultTable = (props) => {
    const classes = useStyles();

    const {
        disabled,
        loading,
        rows,
        children,
        columns,
        actionButton,
        onEdit,
        onDelete,
        onMore,
        onDownload,
        lastButton,
        entryProps,
        title,
        paginationProps,
        size = 'medium',
        orderBy,
        order = 'desc',
        onRequestSort,
        rowVerticalAlign,
        buttonsCellStyle = {},
    } = props;

    //#region  Definição de variáveis auxiliares
    const showLastColumn = actionButton || onEdit || onDelete || onMore || onDownload || lastButton;
    const hasNoChildren = !children || children.length <= 0;
    const hasNoRows = rows?.length <= 0 || !rows;
    //#endregion

    const handleOnMore = (e, data, index) => {
        onMore(e, data, index);
    };

    const handleOnDownload = (e, data, index) => {
        onDownload(e, data, index);
    };

    const handleOnEdit = (e, data, index) => {
        onEdit(e, data, index);
    };

    const handleOnDelete = (e, data, index) => {
        onDelete(e, data, index);
    };

    const handleLastButton = (params) => {
        if (params?.data?.lastButton) {
            params.data.lastButton.onClick(params);
        }
    };

    const createSortHandler = (property) => (event) => {
        onRequestSort && onRequestSort(event, property);
    };

    return (
        <>
            {title && (
                <Box my={2}>
                    <Typography variant="subtitle2">{title}</Typography>
                </Box>
            )}
            <TableContainer>
                <Table size={size}>
                    <TableHead className={classes.tHead}>
                        <TableRow style={{ verticalAlign: 'middle' }}>
                            {columns.map((column, idx) => {
                                return (
                                    <TableCell
                                        key={idx}
                                        className={classes.tHeading}
                                        align={column.align ? column.align : 'inherit'}
                                        style={column.style}>
                                        {orderBy && (
                                            <TableSortLabel
                                                active={orderBy === column.fieldName}
                                                direction={orderBy === column.fieldName ? order : 'desc'}
                                                onClick={createSortHandler(column.fieldName)}>
                                                {column.title}
                                            </TableSortLabel>
                                        )}
                                        {!orderBy && column.title}
                                    </TableCell>
                                );
                            })}
                            {showLastColumn && !disabled && (
                                <TableCell className={classes.tHeading} style={buttonsCellStyle} align="right">
                                    {actionButton && actionButton}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={columns?.length + 1}>
                                    <Box display="flex" justifyContent="center">
                                        <CircularProgress />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && hasNoChildren && hasNoRows && (
                            <TableRow>
                                <TableCell colSpan={columns?.length + 1}>
                                    <Typography component="span" variant="body1">
                                        Nenhum dado foi localizado.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {children}
                        {!loading &&
                            rows?.map((entry, index) => (
                                <TableRow
                                    key={index}
                                    hover
                                    style={{ verticalAlign: rowVerticalAlign || 'middle', ...entry?.style }}>
                                    {columns.map((cell, idx) => (
                                        <TableCell
                                            key={idx}
                                            className={[classes.tData, ...(cell.classes || [])].join(' ')}
                                            align={cell.align ? cell.align : 'inherit'}>
                                            {cell.Component ? (
                                                <cell.Component
                                                    {...entryProps}
                                                    index={index}
                                                    field={cell.fieldName}
                                                    data={entry}
                                                    format={cell.format}
                                                />
                                            ) : (
                                                entry[cell.fieldName]
                                            )}
                                        </TableCell>
                                    ))}
                                    {showLastColumn && !disabled && (
                                        <TableCell style={{ maxWidth: '180px', ...buttonsCellStyle }} align="left">
                                            {onEdit && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => handleOnEdit(event, entry, index)}>
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            {onDownload && (
                                                <IconButton size="small" onClick={handleOnDownload}>
                                                    <DownloadIcon />
                                                </IconButton>
                                            )}
                                            {onDelete && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => handleOnDelete(event, entry, index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                            {onMore && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => handleOnMore(event, entry, index)}>
                                                    <MoreVertIcon />
                                                </IconButton>
                                            )}
                                            {entry.lastButton && (
                                                <Button
                                                    {...entry.lastButton}
                                                    onClick={(event) =>
                                                        handleLastButton({ event, data: entry, index })
                                                    }>
                                                    {entry.lastButton?.label || ''}
                                                </Button>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                    </TableBody>
                    {paginationProps && (
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    {...paginationProps}
                                    ActionsComponent={TablePaginationActions}
                                    SelectProps={{
                                        native: true,
                                    }}
                                />
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    tHead: {
        backgroundColor: theme.palette.primary.main,
    },
    tHeading: {
        color: theme.palette.primary.contrastText,
        textTransform: 'uppercase',
    },
    tData: {
        overflowWrap: 'anywhere',
    },
}));

export default DefaultTable;
